/** A type with a `body` property of type string. */
type HasStringBody = {
  body: string
}
/** A type with a `body` property which contains an `error` property of type string. */
type HasErrorInBody = {
  body: HasStringError
}
/** A type with a `error` property of type string. */
type HasStringError = {
  error: string
}
/** A type with a `message` property of type string. */
type HasStringMessage = {
  message: string
}

/** Checks if an object has a `body` property of type `string`. */
function hasStringBody(error: unknown): error is HasStringBody {
  return (
    typeof error === "object" &&
    error !== null &&
    "body" in error &&
    typeof (error as Record<string, unknown>)["body"] === "string"
  )
}

/** Checks if an object has a `body` property of type `string`. */
function hasErrorInBody(error: unknown): error is HasErrorInBody {
  if (
    typeof error === "object" &&
    error !== null &&
    "body" in error &&
    typeof (error as Record<string, unknown>)["body"] === "object"
  ) {
    const body = error.body
    if (hasStringError(body)) {
      return true
    }
  }
  return false
}

/** Checks if an object has a `error` property of type `string`. */
function hasStringError(error: unknown): error is HasStringError {
  return (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof (error as Record<string, unknown>)["error"] === "string"
  )
}

/** Checks if an object has a `message` property of type `string`. */
function hasStringMessage(error: unknown): error is HasStringMessage {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as Record<string, unknown>)["message"] === "string"
  )
}

/** Ensures that an object has a `message` property of type `string`.
 * @param maybeError
 * @returns If {@linkcode maybeError} has such a property it is returned unchanged. Otherwise a new `Error` object is generated with the serialized {@linkcode maybeError} as its `message`
 */
function toErrorWithMessage(maybeError: unknown): HasStringMessage {
  console.debug("> toErrorWithMessage()", maybeError)
  if (hasStringMessage(maybeError)) {
    console.debug(
      "toErrorWithMessage: hasStringMessage: .message =",
      maybeError.message
    )
    return maybeError
  }
  if (hasStringBody(maybeError)) {
    console.debug("toErrorWithMessage: hasStringBody: .body =", maybeError.body)
    /** @todo maybe detect serialized error property */
    return {
      ...maybeError,
      message: maybeError.body,
    }
  }
  if (hasErrorInBody(maybeError)) {
    console.debug(
      "toErrorWithMessage: hasErrorInBody: .body.error =",
      maybeError.body.error
    )
    return {
      ...maybeError,
      message: maybeError.body.error,
    }
  }
  if (hasStringError(maybeError)) {
    console.debug(
      "toErrorWithMessage: hasStringError: .error =",
      maybeError.error
    )
    return {
      ...maybeError,
      message: maybeError.error,
    }
  }

  if (typeof maybeError === "string") {
    console.debug(
      "toErrorWithMessage: is string; return `new Error()`",
      maybeError
    )
    return new Error(maybeError)
  }

  try {
    console.debug(
      "toErrorWithMessage: try return `new Error(<serialized>)`",
      maybeError
    )
    return new Error(JSON.stringify(maybeError))
  } catch {
    console.debug(
      "toErrorWithMessage: return fallback `new Error(String())",
      maybeError
    )
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError))
  }
}

/** Tries some strategies to extract the message from {@linkcode error} which may be an `Error` object or a `string`.
 * @param error
 * @returns
 */
export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message
}
